import React, { useState, useEffect, useMemo } from 'react';
import { Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';

export type MenuItem = {
  key: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
};

export interface MainMenuProps {
  items: MenuItem[];
  mode?: 'horizontal' | 'vertical' | 'inline';
}

const MainMenu: React.FC<MainMenuProps> = ({ items, mode = 'inline' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Memoize the findActiveKey function to avoid recreating it on every render
  // Determine which menu item should be highlighted based on the current URL.
  // Strategy:
  // 1) Prefer exact path matches
  // 2) Otherwise, choose the item whose path is the longest prefix of the current path
  //    (this keeps parent items highlighted for nested routes like create/view/update)
  const findActiveKey = useMemo(() => {
    const helper = (
      path: string,
      menuItems: MenuItem[]
    ): { key: string; depth: number; matchLen: number } => {
      let best = { key: '', depth: 0, matchLen: 0 };

      for (const item of menuItems) {
        // Exact match wins immediately for this branch
        if (item.path === path) {
          return { key: item.key, depth: 1, matchLen: item.path.length };
        }

        // Prefix match: choose the longest matching path (deepest/most specific)
        // This ensures that /dashboard/user/create still highlights /dashboard/user
        if (item.path !== '/' && path.startsWith(item.path)) {
          if (item.path.length > best.matchLen) {
            best = { key: item.key, depth: 1, matchLen: item.path.length };
          }
        }

        if (item.children) {
          const childBest = helper(path, item.children);
          if (childBest.matchLen > best.matchLen) {
            best = { key: childBest.key, depth: childBest.depth + 1, matchLen: childBest.matchLen };
          }
        }
      }

      return best;
    };

    return (path: string, menuItems: MenuItem[]): string => {
      const result = helper(path, menuItems);
      return result.key;
    };
  }, []);

  // Memoize the active key calculation to avoid recalculating on every render
  const activeKey = useMemo(() => {
    const key = findActiveKey(location.pathname, items);
    return key;
  }, [location.pathname, items]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    setSelectedKeys([activeKey]);
  }, [activeKey]);

  // Memoize the findPathByKey function to avoid recreating it on every render
  const findPathByKey = useMemo(() => {
    return (key: string, menuItems: MenuItem[]): string => {
      for (const item of menuItems) {
        if (item.key === key) {
          return item.path;
        }
        if (item.children) {
          const path = findPathByKey(key, item.children);
          if (path) return path;
        }
      }
      return '';
    };
  }, []);

  const handleClick: MenuProps['onClick'] = (e) => {
    const path = findPathByKey(e.key, items);
    setSelectedKeys([e.key]);
    if (path) {
      navigate(path);
    }
  };

  // Recursively render menu items
  const renderMenuItems = (menuItems: MenuItem[]) => {
    return menuItems.map((item) => {
      if (item.children && item.children.length > 0) {
        return (
          <Menu.SubMenu key={item.key} title={item.label} icon={item.icon}>
            {renderMenuItems(item.children)}
          </Menu.SubMenu>
        );
      }
      return (
        <Menu.Item key={item.key} icon={item.icon}>
          {item.label}
        </Menu.Item>
      );
    });
  };

  const menuStyle = {
    width: '100%',
    display: mode === 'horizontal' ? 'flex' : undefined,
    justifyContent: mode === 'horizontal' ? 'flex-start' : undefined,
  };

  return (
    <Menu
      mode={mode}
      selectedKeys={selectedKeys}
      onClick={handleClick}
      style={menuStyle}
      disabledOverflow={true}
    >
      {renderMenuItems(items)}
    </Menu>
  );
};

export default MainMenu;
